import type { BlogImageSize } from "./blogImageSize";

// Match the 50rem article column and its 1rem padding below md (800px).
export const BLOG_ARTICLE_IMAGE_SIZES = "(min-width: 800px) 50rem, calc(100vw - 2rem)";

export const BLOG_INLINE_IMAGE_SIZES: Record<BlogImageSize, string> = {
  small: "(min-width: 800px) 320px, calc(60vw - 1.2rem)",
  medium: "(min-width: 800px) 520px, calc(85vw - 1.7rem)",
  large: BLOG_ARTICLE_IMAGE_SIZES,
};

export const BLOG_CARD_IMAGE_HEIGHT_REM = 11.875;

const RESPONSIVE_WIDTHS = [320, 480, 640, 800, 1040, 1280, 1600];
const CDN_ORIGINS = new Set(["https://dreamboundcdn.com", "https://dev.dreamboundcdn.com"]);

function isBlogCdnUrl(url: URL): boolean {
  if (CDN_ORIGINS.has(url.origin)) {
    return true;
  }

  // next.config.ts exposes R2_DOMAIN to the client, just as the server's R2
  // URL builder reads it. Match origins exactly so other hosts stay untouched.
  try {
    const configuredCdn = new URL(process.env.R2_DOMAIN ?? "");
    return configuredCdn.protocol === "https:" && url.origin === configuredCdn.origin;
  } catch {
    return false;
  }
}

/**
 * Cloudflare resizes the R2 original at the edge and negotiates AVIF/WebP with
 * format=auto. Keep this client-safe: the server's R2 API also imports the S3
 * client. A bounded srcset lets the browser choose a rendition for its layout
 * and pixel density instead of every blog image downloading the 1600px URL.
 */
export function getBlogImageProps(
  src: string,
  {
    sizes,
    intrinsicWidth,
    fallbackWidth = 800,
  }: { sizes: string; intrinsicWidth?: number | null; fallbackWidth?: number }
): { src: string; srcSet?: string; sizes?: string } {
  // scale-down can return fewer pixels than requested. Without the source
  // width, a width descriptor would lie to the browser and shrink a legacy
  // image's natural size after density correction on high-DPI screens.
  if (!intrinsicWidth || !Number.isFinite(intrinsicWidth) || intrinsicWidth < 1) {
    return { src };
  }

  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return { src };
  }

  // Local Payload files and third-party images retain their existing URLs.
  // SVGs have no raster resolution to reduce.
  if (!isBlogCdnUrl(url) || url.protocol !== "https:" || /\.svg$/i.test(url.pathname)) {
    return { src };
  }

  const transform = url.pathname.match(/^\/cdn-cgi\/image\/([^/]+)(\/.+)$/);
  const sourcePath = transform?.[2] ?? url.pathname;
  const options = new Map<string, string>(
    transform?.[1].split(",").map((option) => {
      const separator = option.indexOf("=");
      return [option.slice(0, separator), option.slice(separator + 1)];
    })
  );

  // Width descriptors must describe the output pixels. Preserve the source
  // aspect ratio, and do not advertise renditions wider than a small upload.
  options.delete("height");
  options.delete("dpr");
  options.set("fit", "scale-down");
  options.set("format", options.get("format") ?? "auto");
  options.set("onerror", options.get("onerror") ?? "redirect");
  const maxWidth = Math.min(1600, Math.floor(intrinsicWidth));
  const widths = [...RESPONSIVE_WIDTHS.filter((width) => width < maxWidth), maxWidth];

  const rendition = (width: number) => {
    options.set("width", String(width));
    const parameters = [...options].map(([key, value]) => `${key}=${value}`).join(",");
    url.pathname = `/cdn-cgi/image/${parameters}${sourcePath}`;
    return url.toString();
  };

  return {
    src: rendition(Math.min(fallbackWidth, maxWidth)),
    srcSet: widths.map((width) => `${rendition(width)} ${width}w`).join(", "),
    sizes,
  };
}

export function getBlogCardImageProps(
  src: string,
  { width, height }: { width?: number | null; height?: number | null }
): { src: string; srcSet?: string; sizes?: string } {
  if (
    !width ||
    !height ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width < 1 ||
    height < 1
  ) {
    return { src };
  }

  // object-fit: cover can draw a wide banner wider than the card's column.
  // Account for that footprint at the fixed card height. sizes resolves rem
  // against initial font metrics, so use pixels for the 13/14/16px root fonts
  // set by GlobalStyle instead of the CSS rem height.
  const coverWidth = (fontSize: number) =>
    `${Math.ceil((BLOG_CARD_IMAGE_HEIGHT_REM * fontSize * width) / height)}px`;
  const sizes = [
    `(max-width: 330px) max(calc(100vw - 30px), ${coverWidth(13)})`,
    `(max-width: 420px) max(calc(100vw - 40px), ${coverWidth(14)})`,
    `(min-width: 1100px) max(350px, ${coverWidth(16)})`,
    `(min-width: 800px) max(calc(50vw - 46px), ${coverWidth(16)})`,
    `max(calc(100vw - 60px), ${coverWidth(16)})`,
  ].join(", ");

  return getBlogImageProps(src, { sizes, intrinsicWidth: width, fallbackWidth: 480 });
}
